import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import AlertSettings from './AlertSettings';
import NotificationSettings from './NotificationSettings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hook to detect mobile screen
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
};

// Format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-SG', { 
    style: 'currency', 
    currency: 'SGD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

// Custom chart colors - PTC brand aligned
const COLORS = {
  navy: '#003366',
  red: '#C8102E',
  green: '#059669',
  yellow: '#D97706',
  purple: '#7C3AED',
  blue: '#0284C7',
  gray: '#64748B'
};

const PIE_COLORS = ['#003366', '#C8102E', '#059669', '#D97706', '#7C3AED', '#0284C7'];

function Dashboard({ token, user }) {
  const isMobile = useIsMobile();
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('mtd');
  const [expandedDept, setExpandedDept] = useState(null);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [showNotificationSettings, setShowNotificationSettings] = useState(false);

  useEffect(() => {
    fetchKPIData();
  }, [period]);

  const fetchKPIData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/dashboard/kpi?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch KPI data');
      const data = await response.json();
      setKpiData(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching KPI data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="spinner" data-testid="dashboard-loading"></div>;
  
  if (error) return (
    <div data-testid="dashboard-error" style={{ padding: '2rem', textAlign: 'center', color: '#DC2626' }}>
      <h3>Error loading dashboard</h3>
      <p>{error}</p>
      <button className="btn btn-primary" onClick={fetchKPIData}>Retry</button>
    </div>
  );

  const { fleet_overview, departments, downtime, revenue_loss, cost_analytics, alerts } = kpiData || {};

  // Prepare chart data
  const departmentChartData = departments?.map(d => ({
    name: d.name?.length > 12 ? d.name.substring(0, 12) + '...' : d.name,
    fullName: d.name,
    active: d.active,
    down: d.down,
    deploymentRate: d.deployment_rate
  })) || [];

  const costByDeptData = Object.entries(cost_analytics?.by_department || {}).map(([name, value]) => ({
    name: name?.length > 15 ? name.substring(0, 15) + '...' : name,
    fullName: name,
    value: value
  }));

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'white',
          border: '1px solid #E2E8F0',
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}>
          <p style={{ fontWeight: '600', marginBottom: '4px', color: '#1E293B' }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color, margin: '2px 0', fontSize: '0.875rem' }}>
              {entry.name}: {entry.name.includes('Rate') ? `${entry.value}%` : 
                entry.name.includes('cost') || entry.name.includes('Cost') ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Stat Card Component
  const StatCard = ({ icon, label, value, subValue, trend, color = COLORS.navy, alert = false }) => (
    <div 
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
      style={{
        background: 'white',
        borderRadius: '16px',
        padding: isMobile ? '1rem' : '1.5rem',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        border: alert ? '2px solid #DC2626' : '1px solid #E2E8F0',
        position: 'relative',
        overflow: 'hidden',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
      }}
    >
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: `linear-gradient(90deg, ${color} 0%, ${color}88 100%)`
      }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <p style={{ 
            fontSize: '0.75rem', 
            fontWeight: '600', 
            textTransform: 'uppercase', 
            letterSpacing: '0.05em',
            color: '#64748B',
            marginBottom: '0.5rem'
          }}>
            {icon} {label}
          </p>
          <p style={{ 
            fontSize: isMobile ? '1.75rem' : '2.25rem', 
            fontWeight: '700', 
            color: color,
            fontFamily: "'JetBrains Mono', monospace",
            lineHeight: 1.1
          }}>
            {value}
          </p>
          {subValue && (
            <p style={{ fontSize: '0.8rem', color: '#64748B', marginTop: '0.25rem' }}>
              {subValue}
            </p>
          )}
        </div>
        {trend && (
          <span style={{
            fontSize: '0.8rem',
            fontWeight: '600',
            color: trend > 0 ? COLORS.green : COLORS.red,
            background: trend > 0 ? '#D1FAE5' : '#FEE2E2',
            padding: '0.25rem 0.5rem',
            borderRadius: '100px'
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
          </span>
        )}
      </div>
    </div>
  );

  // Alert Badge Component
  const AlertBadge = ({ alert }) => {
    const severityColors = {
      high: { bg: '#FEE2E2', border: '#DC2626', text: '#991B1B' },
      medium: { bg: '#FEF3C7', border: '#D97706', text: '#92400E' },
      low: { bg: '#E0F2FE', border: '#0284C7', text: '#075985' }
    };
    const colors = severityColors[alert.severity] || severityColors.medium;
    
    // Alert type icons
    const typeIcons = {
      pending_approval: '⏰',
      long_repair: '🔧',
      high_cost: '💰',
      servicing_overdue: '🛠️',
      servicing_due: '📅'
    };
    
    return (
      <div 
        data-testid={`alert-${alert.type}`}
        style={{
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          borderRadius: '12px',
          padding: '1rem',
          marginBottom: '0.75rem'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
          <div>
            <span style={{ 
              fontWeight: '700', 
              color: colors.text,
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.9rem'
            }}>
              {typeIcons[alert.type] || '⚠️'} {alert.vehicle_no}
            </span>
            <p style={{ color: colors.text, fontSize: '0.85rem', marginTop: '0.25rem' }}>
              {alert.message}
            </p>
            {alert.due_date && (
              <p style={{ fontSize: '0.8rem', color: colors.text, marginTop: '0.25rem' }}>
                📅 Due: {alert.due_date}
              </p>
            )}
          </div>
          <span style={{
            background: colors.border,
            color: 'white',
            fontSize: '0.65rem',
            fontWeight: '700',
            padding: '0.2rem 0.5rem',
            borderRadius: '100px',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap'
          }}>
            {alert.severity}
          </span>
        </div>
        {alert.revenue_loss && (
          <p style={{ fontSize: '0.8rem', color: colors.text, marginTop: '0.5rem' }}>
            💰 Revenue Loss: {formatCurrency(alert.revenue_loss)}
          </p>
        )}
        {alert.cost && !alert.revenue_loss && (
          <p style={{ fontSize: '0.8rem', color: colors.text, marginTop: '0.5rem' }}>
            💰 Cost: {formatCurrency(alert.cost)}
          </p>
        )}
      </div>
    );
  };

  // Department Card Component
  const DepartmentCard = ({ dept }) => {
    const isExpanded = expandedDept === dept.name;
    return (
      <div
        data-testid={`dept-${dept.name}`}
        onClick={() => setExpandedDept(isExpanded ? null : dept.name)}
        style={{
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #E2E8F0',
          padding: '1rem',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h4 style={{ fontWeight: '600', color: '#1E293B', marginBottom: '0.25rem' }}>{dept.name}</h4>
            <p style={{ fontSize: '0.8rem', color: '#64748B' }}>
              {dept.total} vehicles • {dept.deployment_rate}% deployed
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ 
              fontSize: '1.25rem', 
              fontWeight: '700', 
              color: dept.down > 0 ? COLORS.red : COLORS.green 
            }}>
              {dept.down > 0 ? `${dept.down} down` : 'All active'}
            </div>
          </div>
        </div>
        
        {/* Deployment bar */}
        <div style={{ 
          marginTop: '0.75rem', 
          height: '8px', 
          background: '#E2E8F0', 
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <div style={{
            height: '100%',
            width: `${dept.deployment_rate}%`,
            background: dept.deployment_rate > 80 ? COLORS.green : 
                       dept.deployment_rate > 60 ? COLORS.yellow : COLORS.red,
            borderRadius: '4px',
            transition: 'width 0.5s ease'
          }} />
        </div>

        {/* Expanded project breakdown */}
        {isExpanded && dept.projects && dept.projects.length > 0 && (
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #E2E8F0' }}>
            <p style={{ fontSize: '0.75rem', fontWeight: '600', color: '#64748B', marginBottom: '0.5rem' }}>
              PROJECT BREAKDOWN
            </p>
            {dept.projects.map((proj, idx) => (
              <div key={idx} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                padding: '0.5rem 0',
                borderBottom: idx < dept.projects.length - 1 ? '1px dashed #E2E8F0' : 'none'
              }}>
                <span style={{ fontSize: '0.85rem', color: '#334155' }}>{proj.name}</span>
                <span style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '500',
                  color: proj.down > 0 ? COLORS.red : COLORS.green 
                }}>
                  {proj.active}/{proj.total} active
                </span>
              </div>
            ))}
            {dept.revenue_loss_today > 0 && (
              <p style={{ fontSize: '0.85rem', color: COLORS.red, marginTop: '0.75rem', fontWeight: '500' }}>
                💰 Today's Revenue Loss: {formatCurrency(dept.revenue_loss_today)}
              </p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="dashboard-page" style={{ maxWidth: '100%' }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h2 data-testid="dashboard-title" style={{ marginBottom: '0.25rem' }}>Fleet KPI Dashboard</h2>
            <p style={{ color: '#64748B' }}>Real-time fleet performance & analytics</p>
          </div>
          
          {/* Period Selector */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { value: 'mtd', label: 'MTD' },
              { value: '7days', label: '7 Days' },
              { value: '30days', label: '30 Days' },
              { value: 'ytd', label: 'YTD' }
            ].map(p => (
              <button
                key={p.value}
                data-testid={`period-${p.value}`}
                onClick={() => setPeriod(p.value)}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '8px',
                  border: period === p.value ? `2px solid ${COLORS.navy}` : '1px solid #E2E8F0',
                  background: period === p.value ? COLORS.navy : 'white',
                  color: period === p.value ? 'white' : '#64748B',
                  fontWeight: '500',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                {p.label}
              </button>
            ))}
            
            {/* Notification Settings Button */}
            <button
              onClick={() => setShowNotificationSettings(true)}
              data-testid="notification-settings-btn"
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '8px',
                border: '1px solid #E2E8F0',
                background: 'white',
                color: '#64748B',
                fontWeight: '500',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginLeft: '0.5rem'
              }}
              title="Configure scheduled reports and Telegram notifications"
            >
              📬 Notifications
            </button>
          </div>
        </div>
      </div>

      {/* Alert Banner */}
      {alerts && alerts.length > 0 && (
        <div 
          data-testid="alert-banner"
          style={{
            background: 'linear-gradient(90deg, #FEE2E2 0%, #FEF3C7 100%)',
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            marginBottom: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap'
          }}
        >
          <span style={{ 
            background: '#DC2626', 
            color: 'white', 
            padding: '0.35rem 0.75rem', 
            borderRadius: '100px',
            fontWeight: '700',
            fontSize: '0.8rem'
          }}>
            ⚠️ {alerts.length} ALERT{alerts.length > 1 ? 'S' : ''}
          </span>
          <span style={{ color: '#991B1B', fontWeight: '500' }}>
            {alerts.filter(a => a.severity === 'high').length} high priority items require attention
          </span>
        </div>
      )}

      {/* KPI Cards - Fleet Overview */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? '0.75rem' : '1.25rem',
        marginBottom: '2rem'
      }}>
        <StatCard
          icon="🚛"
          label="Total Vehicles"
          value={fleet_overview?.total_vehicles || 0}
          subValue={`${fleet_overview?.vehicles_active || 0} active`}
          color={COLORS.navy}
        />
        <StatCard
          icon="📊"
          label="Deployment Rate"
          value={`${fleet_overview?.deployment_rate || 0}%`}
          subValue={fleet_overview?.deployment_rate >= 90 ? 'Excellent' : fleet_overview?.deployment_rate >= 75 ? 'Good' : 'Needs attention'}
          color={fleet_overview?.deployment_rate >= 90 ? COLORS.green : 
                 fleet_overview?.deployment_rate >= 75 ? COLORS.yellow : COLORS.red}
        />
        <StatCard
          icon="🔧"
          label="Vehicles Down"
          value={fleet_overview?.vehicles_down || 0}
          subValue={`Avg ${downtime?.avg_repair_days || 0} days repair`}
          color={fleet_overview?.vehicles_down > 5 ? COLORS.red : COLORS.yellow}
          alert={fleet_overview?.vehicles_down > 5}
        />
        <StatCard
          icon="💰"
          label="Revenue Loss Today"
          value={formatCurrency(revenue_loss?.today || 0)}
          subValue={`${formatCurrency(revenue_loss?.accumulated || 0)} accumulated`}
          color={COLORS.red}
        />
      </div>

      {/* Cost Analytics Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
        gap: isMobile ? '0.75rem' : '1.25rem',
        marginBottom: '2rem'
      }}>
        <StatCard
          icon="💵"
          label="Total Repair Cost"
          value={formatCurrency(cost_analytics?.total_period || 0)}
          subValue={`${cost_analytics?.repair_count || 0} repairs this period`}
          color={COLORS.navy}
        />
        <StatCard
          icon="📈"
          label="Avg Repair Cost"
          value={formatCurrency(cost_analytics?.avg_repair_cost || 0)}
          color={COLORS.purple}
        />
        <StatCard
          icon="⏱️"
          label="Avg Downtime"
          value={`${downtime?.avg_repair_days || 0} days`}
          subValue="Per vehicle repair"
          color={COLORS.blue}
        />
        <StatCard
          icon="⚠️"
          label="Active Alerts"
          value={alerts?.length || 0}
          subValue={`${alerts?.filter(a => a.severity === 'high').length || 0} high priority`}
          color={alerts?.length > 0 ? COLORS.red : COLORS.green}
          alert={alerts?.filter(a => a.severity === 'high').length > 0}
        />
      </div>

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Monthly Trend Chart */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#1E293B', 
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ 
              width: '4px', 
              height: '18px', 
              background: `linear-gradient(180deg, ${COLORS.navy} 0%, ${COLORS.red} 100%)`,
              borderRadius: '2px'
            }} />
            Monthly Repair Costs
          </h3>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
            <AreaChart data={cost_analytics?.monthly_trend || []}>
              <defs>
                <linearGradient id="colorCost" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.navy} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.navy} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <Tooltip content={<CustomTooltip />} />
              <Area 
                type="monotone" 
                dataKey="cost" 
                name="Repair Cost"
                stroke={COLORS.navy} 
                strokeWidth={2}
                fill="url(#colorCost)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Cost by Department Pie */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#1E293B', 
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ 
              width: '4px', 
              height: '18px', 
              background: `linear-gradient(180deg, ${COLORS.red} 0%, ${COLORS.purple} 100%)`,
              borderRadius: '2px'
            }} />
            Cost by Department
          </h3>
          {costByDeptData.length > 0 ? (
            <ResponsiveContainer width="100%" height={isMobile ? 200 : 250}>
              <PieChart>
                <Pie
                  data={costByDeptData}
                  cx="50%"
                  cy="50%"
                  innerRadius={isMobile ? 40 : 60}
                  outerRadius={isMobile ? 70 : 90}
                  fill="#8884d8"
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {costByDeptData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div style={{ 
              height: 200, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#64748B' 
            }}>
              No cost data for this period
            </div>
          )}
        </div>
      </div>

      {/* Department Deployment Chart */}
      {departmentChartData.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          marginBottom: '2rem'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#1E293B', 
            marginBottom: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ 
              width: '4px', 
              height: '18px', 
              background: `linear-gradient(180deg, ${COLORS.green} 0%, ${COLORS.navy} 100%)`,
              borderRadius: '2px'
            }} />
            Department Vehicle Status
          </h3>
          <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
            <BarChart data={departmentChartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11 }} stroke="#94A3B8" />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar dataKey="active" name="Active" stackId="a" fill={COLORS.green} radius={[0, 0, 0, 0]} />
              <Bar dataKey="down" name="Down" stackId="a" fill={COLORS.red} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Bottom Grid - Departments & Alerts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
        gap: '1.5rem'
      }}>
        {/* Departments List */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#1E293B', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ 
              width: '4px', 
              height: '18px', 
              background: `linear-gradient(180deg, ${COLORS.navy} 0%, ${COLORS.green} 100%)`,
              borderRadius: '2px'
            }} />
            Department Overview
            <span style={{ 
              marginLeft: 'auto', 
              fontSize: '0.75rem', 
              color: '#64748B',
              fontWeight: '400'
            }}>
              Tap to expand
            </span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {departments?.map((dept, idx) => (
              <DepartmentCard key={idx} dept={dept} />
            ))}
            {(!departments || departments.length === 0) && (
              <p style={{ color: '#64748B', textAlign: 'center', padding: '2rem' }}>
                No department data available
              </p>
            )}
          </div>
        </div>

        {/* Alerts Panel */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
        }}>
          <div style={{ 
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <h3 style={{ 
              fontSize: '1rem', 
              fontWeight: '600', 
              color: '#1E293B', 
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span style={{ 
                width: '4px', 
                height: '18px', 
                background: `linear-gradient(180deg, ${COLORS.red} 0%, ${COLORS.yellow} 100%)`,
                borderRadius: '2px'
              }} />
              Active Alerts
              {alerts && alerts.length > 0 && (
                <span style={{
                  marginLeft: '0.5rem',
                  background: '#DC2626',
                  color: 'white',
                  fontSize: '0.7rem',
                  fontWeight: '700',
                  padding: '0.15rem 0.5rem',
                  borderRadius: '100px'
                }}>
                  {alerts.length}
                </span>
              )}
            </h3>
            <button
              onClick={() => setShowAlertSettings(true)}
              style={{
                background: '#F1F5F9',
                border: 'none',
                borderRadius: '8px',
                padding: '0.5rem 0.75rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.8rem',
                fontWeight: '600',
                color: '#64748B',
                transition: 'all 0.2s'
              }}
              data-testid="alert-settings-btn"
              title="Configure alert thresholds"
            >
              ⚙️ Settings
            </button>
          </div>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {alerts?.map((alert, idx) => (
              <AlertBadge key={idx} alert={alert} />
            ))}
            {(!alerts || alerts.length === 0) && (
              <div style={{ 
                textAlign: 'center', 
                padding: '2rem',
                color: '#64748B'
              }}>
                <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>✅</div>
                <p>No active alerts</p>
                <p style={{ fontSize: '0.85rem' }}>All systems operating normally</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Top Vendors Section */}
      {cost_analytics?.top_vendors && cost_analytics.top_vendors.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '1px solid #E2E8F0',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#1E293B', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span style={{ 
              width: '4px', 
              height: '18px', 
              background: `linear-gradient(180deg, ${COLORS.purple} 0%, ${COLORS.blue} 100%)`,
              borderRadius: '2px'
            }} />
            Top Vendors by Spend
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(5, 1fr)',
            gap: '1rem'
          }}>
            {cost_analytics.top_vendors.map((vendor, idx) => (
              <div 
                key={idx}
                style={{
                  background: '#F8FAFC',
                  borderRadius: '12px',
                  padding: '1rem',
                  textAlign: 'center'
                }}
              >
                <div style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: '700', 
                  color: COLORS.navy,
                  marginBottom: '0.25rem'
                }}>
                  #{idx + 1}
                </div>
                <div style={{ 
                  fontSize: '0.85rem', 
                  fontWeight: '500',
                  color: '#334155',
                  marginBottom: '0.5rem',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {vendor.vendor || 'Unknown'}
                </div>
                <div style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '700', 
                  color: COLORS.navy
                }}>
                  {formatCurrency(vendor.spend)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Longest Downtime Vehicle */}
      {downtime?.longest_downtime && (
        <div style={{
          background: 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)',
          borderRadius: '16px',
          padding: '1.5rem',
          border: '2px solid #FECACA',
          marginTop: '1.5rem'
        }}>
          <h3 style={{ 
            fontSize: '1rem', 
            fontWeight: '600', 
            color: '#991B1B', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            🚨 Longest Downtime Vehicle
          </h3>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div>
              <p style={{ 
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '1.5rem',
                fontWeight: '700',
                color: '#991B1B'
              }}>
                {downtime.longest_downtime.vehicle_no}
              </p>
              <p style={{ color: '#92400E', fontSize: '0.9rem' }}>
                {downtime.longest_downtime.department} • {downtime.longest_downtime.description}
              </p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ 
                fontSize: '2rem', 
                fontWeight: '700', 
                color: '#DC2626'
              }}>
                {downtime.longest_downtime.days_down} days
              </p>
              <p style={{ color: '#991B1B', fontWeight: '500' }}>
                Revenue Loss: {formatCurrency(downtime.longest_downtime.revenue_loss)}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alert Settings Modal */}
      {showAlertSettings && (
        <AlertSettings 
          token={token} 
          onClose={() => {
            setShowAlertSettings(false);
            fetchKPIData(); // Refresh data after settings change
          }} 
        />
      )}

      {/* Notification Settings Modal */}
      {showNotificationSettings && (
        <NotificationSettings 
          token={token} 
          onClose={() => setShowNotificationSettings(false)} 
        />
      )}
    </div>
  );
}

export default Dashboard;
